import type React from "react";
import { fmtInt } from "@/lib/format";
import type { Slide, SlideContext } from "../slide.types";
import { Tile } from "../visuals/Tile";
import { Card, CardLabel } from "../visuals/Card";
import { DonutWithLegend, type DonutDatum } from "../visuals/DonutWithLegend";
import { TopBars, type TopBarItem } from "../visuals/TopBars";
import { ACCENT_BLUE, ACCENT_GREEN, ACCENT_ORANGE, ACCENT_RED } from "../colors";

// Testing events are excluded from by_status upstream (see methodology.ts's
// processEmergencyData) and counted separately, so there's no BRAND accent
// reserved for it -- reusing the same neutral gray EmergencyActivity.tsx uses
// for the same reason.
const TESTING_ACCENT = "hsl(0 0% 55%)";

// Mirrors EmergencyActivity.tsx's statusColor() keyword matching, so a given
// status reads as the same color on the dashboard tab and this slide.
function statusColor(status: string): string {
  const s = status.toLowerCase();
  if (s.includes("genuine") || s.includes("emergency")) return ACCENT_RED;
  if (s.includes("deescalat") || s.includes("de-escalat") || s.includes("de escalat")) {
    return ACCENT_ORANGE;
  }
  if (s.includes("not verified") || s.includes("unverified") || s.includes("no answer")) {
    return ACCENT_BLUE;
  }
  if (s.includes("false")) return ACCENT_GREEN;
  return ACCENT_BLUE;
}

function EmergencySlideView({ data, resetKey }: SlideContext): React.JSX.Element {
  const em = data.emergency;
  if (em === null) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-white/40 text-sm font-body">
          No emergency data uploaded for this period.
        </p>
      </div>
    );
  }

  const { total_events, testing_events, by_store, by_device_type, by_status } = em;

  const byDevice: DonutDatum[] = by_device_type
    .map((d) => ({
      name: d.device_type,
      value: d.events,
      color: d.device_type.toLowerCase().includes("falcon") ? ACCENT_BLUE : ACCENT_ORANGE,
    }))
    .filter((d) => d.value > 0);

  const byStatus: DonutDatum[] = [
    ...by_status.map((s) => ({ name: s.status, value: s.events, color: statusColor(s.status) })),
    ...(testing_events > 0
      ? [{ name: "Testing", value: testing_events, color: TESTING_ACCENT }]
      : []),
  ].filter((d) => d.value > 0);
  const statusTotal = byStatus.reduce((sum, d) => sum + d.value, 0);

  const topStores: TopBarItem[] = [...by_store]
    .sort((a, b) => b.events - a.events)
    .slice(0, 5)
    .map((s) => ({
      label: s.store,
      value: s.events,
      display: fmtInt(s.events),
    }));

  const avgPerStore = by_store.length > 0 ? total_events / by_store.length : 0;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
        <Tile
          label="Total Emergency Events"
          value={total_events}
          format={(v) => fmtInt(Math.round(v))}
          delay={100}
          accent={ACCENT_RED}
          resetKey={resetKey}
        />
        <Tile
          label="Teams With Events"
          value={by_store.length}
          format={(v) => fmtInt(Math.round(v))}
          delay={200}
          resetKey={resetKey}
        />
        <Tile
          label="Avg Events / Active Team"
          value={avgPerStore}
          format={(v) => v.toFixed(1)}
          delay={300}
          resetKey={resetKey}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {byDevice.length > 0 ? (
          <DonutWithLegend
            title="Events by Device Type"
            data={byDevice}
            centerValue={total_events}
            centerLabel="events"
            delay={400}
            accentCenter={ACCENT_RED}
          />
        ) : (
          <Card delay={400}>
            <CardLabel>Events by Device Type</CardLabel>
            <p className="text-white/40 text-sm font-body mt-6">
              Device type breakdown not available.
            </p>
          </Card>
        )}
        {byStatus.length > 0 ? (
          <DonutWithLegend
            title="Events by Status"
            data={byStatus}
            centerValue={statusTotal}
            centerLabel="events"
            delay={450}
            accentCenter={ACCENT_RED}
          />
        ) : (
          <Card delay={450}>
            <CardLabel>Events by Status</CardLabel>
            <p className="text-white/40 text-sm font-body mt-6">Status breakdown not available.</p>
          </Card>
        )}
        <TopBars
          title="Top Teams by Emergency Events"
          items={topStores}
          delay={500}
          accentTop={ACCENT_RED}
          accentRest={ACCENT_ORANGE}
        />
      </div>
    </>
  );
}

export const emergencySlide: Slide = {
  id: "emergency",
  title: "Emergency Activity",
  render: (ctx) => <EmergencySlideView {...ctx} />,
};
